import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { registerSW } from 'virtual:pwa-register';
import { router } from '@app/router';
import { queryClient, queryPersister } from '@app/query/queryClient';
import { AuthProvider } from '@/context/AuthContext';
import { SelectedStationProvider } from '@features/stations/context/SelectedStationProvider';
import { AppCompatibilityProvider } from '@features/appVersion/context/AppCompatibilityProvider';
import { setServiceWorkerUpdater } from '@services/pwa/updateController';
import { registerForcedUpdateBackupHandler } from '@services/offline/exportSyncQueue';
import '@app/i18n/i18n';
import '@app/styles/global.css';

// Capture the real `updateSW` so the controlled update flow (C.6) can apply the
// waiting worker on demand instead of relying on autoUpdate's implicit refresh.
const updateSW = registerSW({ immediate: true });
setServiceWorkerUpdater(updateSW);

// Keep a live snapshot of the sync queue and register the synchronous forced-update
// backup handler (C.7) so a forced update can offer a legal backup of pending
// critical operations before reloading onto a new build.
registerForcedUpdateBackupHandler();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        maxAge: 1000 * 60 * 60 * 24
      }}
    >
      <AuthProvider>
        <SelectedStationProvider>
          <AppCompatibilityProvider>
            <RouterProvider router={router} />
          </AppCompatibilityProvider>
        </SelectedStationProvider>
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
    </PersistQueryClientProvider>
  </React.StrictMode>
);
